/* global vis */
import React, { Component } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Paper, Typography, Button, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Tab } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { connect } from 'react-redux';
import CardPage from '../../../commonModules/layouts/CardPage';
import Currency from '../../../commonModules/Currency';
import { isEmpty } from 'lodash';
import PriceEvolutionChart from './PriceEvolutionChart';

// Component wrapper to use hooks
const RFIDGraphWrapper = (props) => {
    const { rfid: rfidFromUrl } = useParams();
    const [searchParams] = useSearchParams();
    const productId = searchParams.get('product_id');
    const transferId = searchParams.get('transfer_id');
    const historyId = searchParams.get('history_id');
    
    return <RFIDGraph rfidFromUrl={rfidFromUrl} productId={productId} transferId={transferId} historyId={historyId} {...props} />;
};

class RFIDGraph extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            error: null,
            graphData: null,
            network: null,
            selectedHistory: null,
            selectedEdge: null,
            rfidInput: props.rfidFromUrl || '',
            rfidList: [],
            loadingList: false,
            listError: null,
            productId: props.productId || null,
            transferId: props.transferId || null,
            historyId: props.historyId || null,
            searchInput: '', // Champ de recherche pour history_id ou transfer_id
            filteredBranch: null, // Store filtered branch data
            showingBranch: false, // Track if branch filter is active
            transferTabValue: 0, // Index de l'onglet actif pour les détails du transfer (0 = Détails, 1 = RFIDHistory)
            transferRFIDHistory: [], // Liste des RFIDHistory du transfer sélectionné
            loadingTransferHistory: false, // Loading state pour le chargement des RFIDHistory
            transferHistoryError: null, // Error state pour les RFIDHistory
            productNamesCache: {}, // Cache pour les noms de produits (product_id -> name)
            showDeletedNodes: true // Toggle pour afficher/masquer les nœuds supprimés
        };
        this.networkContainerRef = React.createRef();
    }

    componentDidMount() {
        // Load vis.js from CDN first
        this.loadVisJs();
        
        // Load RFID list only if productId is provided
        if (this.props.productId) {
            this.loadRFIDList();
        }
        
        if (this.props.rfidFromUrl) {
            this.loadGraphData(this.props.rfidFromUrl);
        }
    }

    componentDidUpdate(prevProps, prevState) {
        // If productId changed, reload RFID list only if productId exists
        if (this.props.productId !== prevProps.productId) {
            this.setState({ productId: this.props.productId || null });
            if (this.props.productId) {
                this.loadRFIDList();
            } else {
                // Clear the list if productId is removed
                this.setState({ rfidList: [], listError: null });
            }
        }
        
        // Update transferId and historyId from props
        if (this.props.transferId !== prevProps.transferId || this.props.historyId !== prevProps.historyId) {
            this.setState({ 
                transferId: this.props.transferId || null,
                historyId: this.props.historyId || null
            }, () => {
                // If graph is already rendered, zoom on the new element
                if (this.state.network && this.state.graphData && typeof window.vis !== 'undefined') {
                    // Rebuild transferId to edge index mapping
                    const transferIdToEdgeIndex = {};
                    if (this.state.graphData.edges && Array.isArray(this.state.graphData.edges)) {
                        this.state.graphData.edges.forEach((edge, index) => {
                            if (edge && edge.transfer_id) {
                                transferIdToEdgeIndex[edge.transfer_id] = index;
                            }
                        });
                    }
                    this.zoomOnElement(this.state.network, this.state.graphData, transferIdToEdgeIndex);
                }
            });
        }
        
        // If graphData changed and vis.js is loaded, render the graph
        if (this.state.graphData && 
            this.state.graphData !== prevState.graphData && 
            typeof window.vis !== 'undefined') {
            this.renderGraph(this.state.graphData);
        }
        
        // If vis.js just loaded and we have graphData, render it
        if (prevState.loading && !this.state.loading && 
            this.state.graphData && 
            typeof window.vis !== 'undefined') {
            this.renderGraph(this.state.graphData);
        }
    }

    loadVisJs() {
        if (typeof window.vis !== 'undefined') {
            return; // Already loaded
        }

        const script = document.createElement('script');
        script.src = 'https://unpkg.com/vis-network/standalone/umd/vis-network.min.js';
        script.onload = () => {
            if (this.state.graphData) {
                this.renderGraph(this.state.graphData);
            }
        };
        document.head.appendChild(script);
    }

    async loadGraphData(rfid) {
        this.setState({ loading: true, error: null });

        try {
            const response = await fetch(`/api/rfid/history/graph/${rfid}`, {
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            // Read response once - check content type first
            const contentType = response.headers.get('content-type');
            const isJson = contentType && contentType.includes('application/json');
            
            let data;
            if (isJson) {
                // Read the response body once
                const responseText = await response.text();
                try {
                    data = JSON.parse(responseText);
                } catch (parseError) {
                    console.error('Failed to parse JSON:', parseError, 'Response:', responseText);
                    throw new Error(`Erreur de parsing JSON: ${parseError.message}`);
                }
            } else {
                // Non-JSON response
                const text = await response.text();
                throw new Error(`Réponse inattendue (${contentType || 'unknown'}): ${text.substring(0, 200)}`);
            }

            // Now check if it's an error response
            if (!response.ok) {
                const errorMessage = data.error || `Erreur HTTP ${response.status}`;
                throw new Error(errorMessage);
            }

            console.log('RFID Graph data received:', data);
            
            // Log tous les HistoryID reçus pour diagnostic
            if (data && data.nodes && Array.isArray(data.nodes)) {
                const historyIDs = data.nodes.map(node => node.history_id);
                console.log(`Total nodes reçus: ${data.nodes.length}, HistoryIDs:`, historyIDs);
            }
            
            // Validate response data
            if (!data) {
                throw new Error('Aucune donnée reçue du serveur');
            }
            
            if (!data.nodes || !Array.isArray(data.nodes)) {
                console.error('Invalid graph data structure:', data);
                throw new Error(`Format de données invalide: nodes manquant ou invalide. Reçu: ${JSON.stringify(data)}`);
            }

            // Store original data when loading
            this.originalGraphData = data;
            
            this.setState({ graphData: data, loading: false, showingBranch: false, filteredBranch: null });
            
            // Render graph if vis.js is loaded
            if (typeof window.vis !== 'undefined') {
                this.renderGraph(data);
            } else {
                // Wait a bit for vis.js to load
                const checkVisLoaded = setInterval(() => {
                    if (typeof window.vis !== 'undefined') {
                        clearInterval(checkVisLoaded);
                        this.renderGraph(data);
                    }
                }, 100);
                
                // Timeout after 5 seconds
                setTimeout(() => {
                    clearInterval(checkVisLoaded);
                    if (typeof window.vis === 'undefined') {
                        this.setState({ 
                            error: 'Erreur: vis.js n\'a pas pu être chargé',
                            loading: false 
                        });
                    }
                }, 5000);
            }
        } catch (error) {
            console.error('Error loading graph data:', error);
            this.setState({ 
                error: error.message || 'Erreur lors du chargement des données du graphique',
                loading: false 
            });
        }
    }

    renderGraph(data) {
        if (!this.networkContainerRef.current) {
            return;
        }

        // Validate data structure
        if (!data || typeof data !== 'object') {
            console.error('Invalid graph data:', data);
            this.setState({ 
                error: 'Données du graphique invalides',
                loading: false 
            });
            return;
        }

        const graphNodes = data.nodes || [];
        const graphEdges = data.edges || [];

        // Validate that we have nodes
        if (!Array.isArray(graphNodes) || graphNodes.length === 0) {
            console.error('No nodes found in graph data:', data);
            this.setState({ 
                error: 'Aucun historique trouvé pour ce RFID',
                loading: false 
            });
            return;
        }

        // Check if vis.js is loaded
        if (typeof window.vis === 'undefined') {
            console.error('vis.js is not loaded');
            return;
        }

        // Log nodes for debugging
        console.log('Total history entries:', graphNodes.length);
        console.log('Total connections:', graphEdges.length);
        const allHistoryIDs = graphNodes.map(node => node.history_id);
        console.log('All HistoryIDs in graph:', allHistoryIDs);
        
        // Store node data mapping for later access
        const nodeDataMap = {};
        graphNodes.forEach(node => {
            nodeDataMap[node.history_id] = node;
        });

        // Store edge data mapping for later access
        const edgeDataMap = {};
        graphEdges.forEach((edge, index) => {
            edgeDataMap[index] = edge;
        });
        
        // Store transfer_id to edge index mapping for quick lookup
        const transferIdToEdgeIndex = {};
        graphEdges.forEach((edge, index) => {
            if (edge.transfer_id) {
                transferIdToEdgeIndex[edge.transfer_id] = index;
            }
        });

        // Filtrer les nœuds selon showDeletedNodes
        // Le nœud origin est toujours inclus
        const filteredNodes = this.state.showDeletedNodes 
            ? graphNodes 
            : graphNodes.filter(node => !node.is_deleted || node.history_id === 'origin-node');

        // Create nodes for vis.js
        const nodes = new window.vis.DataSet(
            filteredNodes.map(node => {
                const priceUnitTTC = this.calculatePriceUnitTTC(node.price_ttc, node.quantity);
                let title = `History: ${node.history_id}\nRFID: ${node.rfid}\nProduit: ${node.product_name}\nShop: ${node.shop_name}\nType: ${node.op_type}\nDate: ${node.date}\nQuantité: ${node.quantity}\nPrix U: ${this.formatPriceUnit(node.price_u || 0)}\nPrix U TTC: ${this.formatPriceUnit(priceUnitTTC)}\nPrix HT: ${this.formatCurrencyString(node.price_ht || 0)}\nPrix TTC: ${this.formatCurrencyString(node.price_ttc || 0)}\nCoût: ${this.formatCurrencyString(node.cost || 0)}`;
                
                // Ajouter Surcout (toujours affiché)
                title += `\nSurcout: ${(node.surcout || 0).toFixed(2)}`;
                
                // Ajouter Transport et Taxes (toujours affichés, même à 0)
                const transportDisplay = node.transport_total ? 
                    `${this.formatCurrencyString(node.transport || 0)} (${this.formatCurrencyString(node.transport_total)})` :
                    this.formatCurrencyString(node.transport || 0);
                const taxesDisplay = node.taxes_total ? 
                    `${this.formatCurrencyString(node.taxes || 0)} (${this.formatCurrencyString(node.taxes_total)})` :
                    this.formatCurrencyString(node.taxes || 0);
                title += `\nTransport: ${transportDisplay}`;
                title += `\nTaxes: ${taxesDisplay}`;
                
                // Ajouter les informations de destination pour reapprovisionnement_ext
                if (node.op_type === 'reapprovisionnement_ext') {
                    if (node.dest_shop_name || node.dest_account_name) {
                        if (node.dest_shop_name) {
                            title += `\nShop Destination: ${node.dest_shop_name}`;
                        }
                        if (node.dest_account_name) {
                            title += `\nCompte Destination: ${node.dest_account_name}`;
                        }
                    }
                }
                
                title += `\nStatus: ${node.status}`;
                if (node.is_deleted) {
                    title += `\n⚠️ SUPPRIMÉ`;
                }
                
                return {
                    id: node.history_id,
                    label: `${node.shop_name}\n${node.op_type}\n${node.date}\n${this.formatCurrencyString(node.cost || 0)}\nQté: ${node.quantity}${node.is_deleted ? '\n[SUPPRIMÉ]' : ''}`,
                    title: title,
                    color: this.getNodeColor(node.op_type, node.available, node.is_deleted),
                    shape: 'box',
                    font: {
                        size: 12,
                        color: node.is_deleted ? '#666' : 'white',
                        bold: true
                    },
                    borderWidth: node.available ? 3 : 1,
                    borderColor: node.is_deleted ? '#666' : undefined,
                    shadow: {
                        enabled: true
                    },
                    opacity: node.is_deleted ? 0.5 : 1
                };
            })
        );

        // Filtrer les edges pour ne garder que ceux connectant les nœuds visibles
        const visibleNodeIds = new Set(filteredNodes.map(node => node.history_id));
        const filteredEdges = graphEdges.filter(edge => 
            visibleNodeIds.has(edge.from) && visibleNodeIds.has(edge.to)
        );

        // Create edges for vis.js
        const edges = new window.vis.DataSet(
            filteredEdges.map((edge, index) => {
                const priceUnitTTC = this.calculatePriceUnitTTC(edge.price_ttc, edge.quantity);
                let title = `Transfer: ${edge.transfer_id || 'N/A'}\nType: ${edge.op_type || ''}\nDate: ${edge.date || ''}\nQuantité: ${edge.quantity || 0}\nPrix U: ${this.formatPriceUnit(edge.price_u || 0)}\nPrix U TTC: ${this.formatPriceUnit(priceUnitTTC)}\nPrix HT: ${this.formatCurrencyString(edge.price_ht || 0)}\nPrix TTC: ${this.formatCurrencyString(edge.price_ttc || 0)}\nCoût: ${this.formatCurrencyString(edge.cost || 0)}`;
                
                // Ajouter Surcout (toujours affiché)
                title += `\nSurcout: ${(edge.surcout || 0).toFixed(2)}`;
                
                // Ajouter Transport et Taxes (toujours affichés, même à 0)
                const edgeTransportDisplay = edge.transport_total ? 
                    `${this.formatCurrencyString(edge.transport || 0)} (${this.formatCurrencyString(edge.transport_total)})` :
                    this.formatCurrencyString(edge.transport || 0);
                const edgeTaxesDisplay = edge.taxes_total ? 
                    `${this.formatCurrencyString(edge.taxes || 0)} (${this.formatCurrencyString(edge.taxes_total)})` :
                    this.formatCurrencyString(edge.taxes || 0);
                title += `\nTransport: ${edgeTransportDisplay}`;
                title += `\nTaxes: ${edgeTaxesDisplay}`;
                
                return {
                    id: index,
                    from: edge.from,
                    to: edge.to,
                    label: edge.label || '',
                    title: title,
                    arrows: 'to',
                    color: { color: '#2196F3' },
                    width: 2,
                    font: {
                        size: 10,
                        color: '#333',
                        background: 'rgba(255, 255, 255, 0.8)',
                        strokeWidth: 3,
                        strokeColor: 'white',
                        align: 'top'
                    },
                    smooth: {
                        type: 'cubicBezier',
                        roundness: 0.2
                    }
                };
            })
        );

        // Network options
        const options = {
            layout: {
                hierarchical: {
                    enabled: true,
                    direction: 'LR', // Left to Right
                    sortMethod: 'directed',
                    levelSeparation: 400,
                    nodeSpacing: 200,
                    treeSpacing: 250,
                    blockShifting: true,
                    edgeMinimization: true,
                    parentCentralization: true
                }
            },
            physics: {
                enabled: false
            },
            edges: {
                font: {
                    size: 10,
                    align: 'top',
                    color: '#333',
                    background: 'rgba(255, 255, 255, 0.8)',
                    strokeWidth: 3,
                    strokeColor: 'white'
                },
                arrows: {
                    to: {
                        enabled: true,
                        scaleFactor: 1.2
                    }
                }
            },
            nodes: {
                widthConstraint: {
                    minimum: 180,
                    maximum: 250
                },
                margin: 15
            },
            interaction: {
                hover: true,
                tooltipDelay: 100,
                dragNodes: true,
                dragView: true,
                zoomView: true
            }
        };

        // Create network
        const container = this.networkContainerRef.current;
        const networkData = { nodes, edges };
        const network = new window.vis.Network(container, networkData, options);

        // Handle node and edge clicks
        network.on('click', (params) => {
            if (params.nodes.length > 0) {
                // Click on a node
                const nodeId = params.nodes[0];
                const node = nodeDataMap[nodeId];
                if (node) {
                    this.setState({ selectedHistory: node, selectedEdge: null });
                }
            } else if (params.edges.length > 0) {
                // Click on an edge
                const edgeId = params.edges[0];
                const edge = edgeDataMap[edgeId];
                if (edge) {
                    this.setState({ selectedEdge: edge, selectedHistory: null, transferTabValue: 0 });
                    // Charger les RFIDHistory du transfer
                    if (edge.transfer_id) {
                        this.loadTransferRFIDHistory(edge.transfer_id);
                    }
                }
            } else {
                // Click on empty space - clear selection
                this.setState({ selectedHistory: null, selectedEdge: null });
            }
        });

        // Center view after rendering, then zoom on specific element if requested
        network.once('afterDrawing', () => {
            // First, fit the entire graph
            network.fit({
                animation: {
                    duration: 500,
                    easingFunction: 'easeInOutQuad'
                }
            });
            
            // Then, if a specific history_id or transfer_id is requested, zoom on it
            setTimeout(() => {
                this.zoomOnElement(network, data, transferIdToEdgeIndex);
            }, 600); // Wait for initial fit animation to complete
        });

        this.setState({ network });
    }

    formatCurrencyString(value) {
        const currency = this.props.currency || 'Euro';
        const numValue = parseFloat(value) || 0;
        
        let formattedValue;
        let symbol;
        
        if (currency === 'Euro') {
            formattedValue = (Math.round(numValue * 100) / 100).toFixed(2);
            symbol = '€';
        } else {
            formattedValue = Math.round(numValue).toLocaleString('fr-FR');
            symbol = 'FCFA';
        }
        
        return `${formattedValue} ${symbol}`;
    }

    formatPriceUnit(value) {
        const currency = this.props.currency || 'Euro';
        const numValue = parseFloat(value) || 0;
        
        let formattedValue;
        let symbol;
        
        if (currency === 'Euro') {
            formattedValue = (Math.round(numValue * 100) / 100).toFixed(2);
            symbol = '€';
        } else {
            // For FCFA, also show 2 decimal places for unit prices
            formattedValue = (Math.round(numValue * 100) / 100).toFixed(2);
            symbol = 'FCFA';
        }
        
        return `${formattedValue} ${symbol}`;
    }

    calculatePriceUnitTTC(priceTTC, quantity) {
        const priceTTCValue = parseFloat(priceTTC) || 0;
        const quantityValue = parseFloat(quantity) || 0;
        
        if (quantityValue === 0) {
            return 0;
        }
        
        return priceTTCValue / quantityValue;
    }

    getNodeColor(opType, available, isDeleted) {
        // Color code by operation type
        const colors = {
            'reapprovisionnement': '#4CAF50',
            'reservation': '#2196F3',
            'installation': '#FF9800',
            'vente': '#E91E63',
            'payg': '#9C27B0',
            'achat': '#00BCD4',
            'commercial': '#795548',
            'commercial_return': '#FF3D00', // Rouge-orange vif pour les retours commerciaux (distinct de l'orange installation)
            'avoir': '#3F51B5', // Indigo pour les avoirs (distinct du teal/cyan existant)
            'reapprovisionnement_ext': '#607D8B',
            'arrivage': '#009688',
            'demande': '#FFC107',
            'sav': '#F44336'
        };
        
        let color = colors[opType] || '#757575';
        
        // Griser si supprimé
        if (isDeleted) {
            color = '#9E9E9E'; // Gris pour les nœuds supprimés
        } else if (!available) {
            color = color + '80'; // Add alpha for transparency
        }
        
        return color;
    }

    zoomOnElement(network, graphData, transferIdToEdgeIndex) {
        const { historyId, transferId } = this.state;
        
        if (historyId) {
            // Find the node with this history_id
            const targetNode = graphData.nodes.find(node => node.history_id === historyId);
            if (targetNode) {
                // Select and focus on the node
                network.selectNodes([targetNode.history_id]);
                network.focus(targetNode.history_id, {
                    animation: {
                        duration: 800,
                        easingFunction: 'easeInOutQuad'
                    },
                    scale: 1.5 // Zoom level
                });
                
                // Update state to show details
                this.setState({ 
                    selectedHistory: targetNode, 
                    selectedEdge: null 
                });
                
                console.log(`Zoomed on history_id: ${historyId}`);
            } else {
                console.warn(`History ID ${historyId} not found in graph`);
            }
        } else if (transferId) {
            // Find the edge with this transfer_id
            const edgeIndex = transferIdToEdgeIndex[transferId];
            if (edgeIndex !== undefined) {
                const targetEdge = graphData.edges[edgeIndex];
                if (targetEdge) {
                    // Select the edge
                    network.selectEdges([edgeIndex]);
                    
                    // Focus on the "to" node (destination) of the edge
                    network.focus(targetEdge.to, {
                        animation: {
                            duration: 800,
                            easingFunction: 'easeInOutQuad'
                        },
                        scale: 1.5 // Zoom level
                    });
                    
                    // Update state to show details
                    this.setState({ 
                        selectedEdge: targetEdge, 
                        selectedHistory: null,
                        transferTabValue: 0
                    });
                    
                    // Charger les RFIDHistory du transfer
                    if (targetEdge.transfer_id) {
                        this.loadTransferRFIDHistory(targetEdge.transfer_id);
                    }
                    
                    console.log(`Zoomed on transfer_id: ${transferId}`);
                }
            } else {
                console.warn(`Transfer ID ${transferId} not found in graph`);
            }
        }
    }

    changeLayout(direction) {
        if (this.state.network) {
            const layoutOptions = {
                layout: {
                    hierarchical: {
                        enabled: true,
                        direction: direction,
                        sortMethod: 'directed',
                        levelSeparation: direction === 'UD' || direction === 'DU' ? 300 : 400,
                        nodeSpacing: direction === 'UD' || direction === 'DU' ? 200 : 200,
                        treeSpacing: 250,
                        blockShifting: true,
                        edgeMinimization: true,
                        parentCentralization: true
                    }
                }
            };
            
            this.state.network.setOptions(layoutOptions);
            
            setTimeout(() => {
                this.state.network.fit({
                    animation: {
                        duration: 500,
                        easingFunction: 'easeInOutQuad'
                    }
                });
            }, 100);
        }
    }

    handleRFIDInputChange = (event) => {
        this.setState({ rfidInput: event.target.value });
    }

    handleRFIDSubmit = (event) => {
        event.preventDefault();
        const rfid = this.state.rfidInput.trim();
        if (rfid) {
            this.loadGraphData(rfid);
        }
    }

    async loadRFIDList() {
        this.setState({ loadingList: true, listError: null });

        try {
            // Build URL with optional product_id filter
            // Use props.productId to ensure we have the latest value
            const productId = this.props.productId || this.state.productId;
            let url = `/api/rfid/list`;
            if (productId) {
                url += `?product_id=${encodeURIComponent(productId)}`;
            }
            
            const response = await fetch(url, {
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const contentType = response.headers.get('content-type');
            const isJson = contentType && contentType.includes('application/json');
            
            let data;
            if (isJson) {
                const responseText = await response.text();
                try {
                    data = JSON.parse(responseText);
                } catch (parseError) {
                    console.error('Failed to parse JSON:', parseError, 'Response:', responseText);
                    throw new Error(`Erreur de parsing JSON: ${parseError.message}`);
                }
            } else {
                const text = await response.text();
                throw new Error(`Réponse inattendue (${contentType || 'unknown'}): ${text.substring(0, 200)}`);
            }

            if (!response.ok) {
                const errorMessage = data.error || `Erreur HTTP ${response.status}`;
                throw new Error(errorMessage);
            }

            if (!Array.isArray(data)) {
                throw new Error('Format de données invalide: array attendu');
            }

            console.log('RFID List data received:', data.length, 'items');
            this.setState({ rfidList: data, loadingList: false });
        } catch (error) {
            console.error('Error loading RFID list:', error);
            this.setState({ 
                listError: error.message || 'Erreur lors du chargement de la liste des RFID',
                loadingList: false 
            });
        }
    }

    handleRFIDClick = (rfid) => {
        this.setState({ rfidInput: rfid });
        this.loadGraphData(rfid);
    }

    async loadTransferRFIDHistory(transferId) {
        if (!transferId || transferId.trim() === '') {
            console.warn('loadTransferRFIDHistory: transferId is empty or null');
            this.setState({ 
                transferHistoryError: 'Transfer ID est vide',
                loadingTransferHistory: false,
                transferRFIDHistory: []
            });
            return;
        }

        console.log('Loading RFIDHistory for transfer:', transferId);
        this.setState({ loadingTransferHistory: true, transferHistoryError: null });

        try {
            const response = await fetch(`/api/transfers/${encodeURIComponent(transferId)}`, {
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error(`Error response (${response.status}):`, errorText);
                throw new Error(`Erreur HTTP ${response.status}: ${errorText.substring(0, 200)}`);
            }

            const transfer = await response.json();
            
            // Les RFIDHistory sont dans transfer.products (minuscule, car tag JSON = "products")
            const rfidHistory = transfer.products || transfer.Products || [];
            console.log(`Loaded ${rfidHistory.length} RFIDHistory items for transfer ${transferId}`, transfer);
            
            // Récupérer les noms de produits pour tous les product_id uniques
            this.loadProductNames(rfidHistory);
            
            this.setState({ 
                transferRFIDHistory: rfidHistory, 
                loadingTransferHistory: false 
            });
        } catch (error) {
            console.error('Error loading transfer RFID history:', error);
            this.setState({ 
                transferHistoryError: error.message || 'Erreur lors du chargement des RFIDHistory',
                loadingTransferHistory: false,
                transferRFIDHistory: []
            });
        }
    }

    handleTransferTabChange = (event, newValue) => {
        this.setState({ transferTabValue: newValue });
    }

    async loadProductNames(rfidHistoryList) {
        // Extraire tous les product_id uniques
        const productIds = [...new Set(rfidHistoryList
            .map(h => h.product_id)
            .filter(id => id && id !== 'N/A')
        )];

        if (productIds.length === 0) {
            return;
        }

        // Récupérer les noms de produits manquants dans le cache
        const missingProductIds = productIds.filter(id => !this.state.productNamesCache[id]);
        
        if (missingProductIds.length === 0) {
            return; // Tous les noms sont déjà en cache
        }

        console.log(`Loading ${missingProductIds.length} product names...`);

        // Charger les noms de produits en parallèle
        const productNamePromises = missingProductIds.map(async (productId) => {
            try {
                const response = await fetch('/stocks/single', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        page: 'products',
                        id: productId
                    }),
                    credentials: 'same-origin'
                });

                if (response.ok) {
                    const product = await response.json();
                    return { productId, name: product.name || productId };
                } else {
                    return { productId, name: productId };
                }
            } catch (error) {
                console.error(`Error loading product ${productId}:`, error);
                return { productId, name: productId };
            }
        });

        const productNames = await Promise.all(productNamePromises);
        
        // Mettre à jour le cache
        const newCache = { ...this.state.productNamesCache };
        productNames.forEach(({ productId, name }) => {
            newCache[productId] = name;
        });

        this.setState({ productNamesCache: newCache });
    }

    getProductName(productId) {
        if (!productId) return 'N/A';
        return this.state.productNamesCache[productId] || productId;
    }

    handleSearchInputChange = (event) => {
        this.setState({ searchInput: event.target.value });
    }

    handleSearchSubmit = (event) => {
        event.preventDefault();
        const searchValue = this.state.searchInput.trim();
        if (!searchValue) {
            return;
        }

        if (!this.state.network || !this.state.graphData) {
            this.setState({ 
                error: 'Veuillez d\'abord charger un graphique RFID' 
            });
            return;
        }

        // Clear previous error
        this.setState({ error: null });

        // Rebuild transferId to edge index mapping
        const transferIdToEdgeIndex = {};
        if (this.state.graphData.edges && Array.isArray(this.state.graphData.edges)) {
            this.state.graphData.edges.forEach((edge, index) => {
                if (edge && edge.transfer_id) {
                    transferIdToEdgeIndex[edge.transfer_id] = index;
                }
            });
        }

        // Try to find as history_id first (exact match preferred, then partial)
        let targetNode = this.state.graphData.nodes.find(
            node => node.history_id === searchValue
        );
        
        if (!targetNode) {
            // Try partial match
            targetNode = this.state.graphData.nodes.find(
                node => node.history_id && node.history_id.includes(searchValue)
            );
        }
        
        if (targetNode) {
            // Found as history_id
            this.state.network.selectNodes([targetNode.history_id]);
            this.state.network.focus(targetNode.history_id, {
                animation: {
                    duration: 800,
                    easingFunction: 'easeInOutQuad'
                },
                scale: 1.5
            });
            this.setState({ 
                selectedHistory: targetNode, 
                selectedEdge: null,
                historyId: targetNode.history_id,
                transferId: null,
                error: null
            });
            return;
        }

        // Try to find as transfer_id (exact match preferred, then partial)
        let matchingTransferIds = Object.keys(transferIdToEdgeIndex).filter(
            tid => tid === searchValue
        );
        
        if (matchingTransferIds.length === 0) {
            // Try partial match
            matchingTransferIds = Object.keys(transferIdToEdgeIndex).filter(
                tid => tid.includes(searchValue)
            );
        }
        
        if (matchingTransferIds.length > 0) {
            // Use the first match (exact match if available)
            const exactMatch = matchingTransferIds.find(tid => tid === searchValue);
            const transferId = exactMatch || matchingTransferIds[0];
            const edgeIndex = transferIdToEdgeIndex[transferId];
            const targetEdge = this.state.graphData.edges[edgeIndex];
            
            if (targetEdge) {
                this.state.network.selectEdges([edgeIndex]);
                this.state.network.focus(targetEdge.to, {
                    animation: {
                        duration: 800,
                        easingFunction: 'easeInOutQuad'
                    },
                    scale: 1.5
                });
                this.setState({ 
                    selectedEdge: targetEdge, 
                    selectedHistory: null,
                    transferId: transferId,
                    historyId: null,
                    error: null,
                    transferTabValue: 0
                });
                
                // Charger les RFIDHistory du transfer
                if (targetEdge.transfer_id) {
                    this.loadTransferRFIDHistory(targetEdge.transfer_id);
                }
                return;
            }
        }

        // Not found
        this.setState({ 
            error: `Aucun élément trouvé avec l'ID: ${searchValue}` 
        });
    }

    formatDate(dateString) {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('fr-FR', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (e) {
            return dateString;
        }
    }

    // Get all children of a node (recursively)
    getAllChildren(nodeId, edges, visited = new Set()) {
        if (visited.has(nodeId)) return new Set();
        visited.add(nodeId);
        
        const children = new Set();
        edges.forEach(edge => {
            if (edge.from === nodeId) {
                children.add(edge.to);
                // Recursively get children of children
                const subChildren = this.getAllChildren(edge.to, edges, visited);
                subChildren.forEach(child => children.add(child));
            }
        });
        return children;
    }

    // Get all parents of a node (recursively)
    getAllParents(nodeId, edges, visited = new Set()) {
        if (visited.has(nodeId)) return new Set();
        visited.add(nodeId);
        
        const parents = new Set();
        edges.forEach(edge => {
            if (edge.to === nodeId) {
                parents.add(edge.from);
                // Recursively get parents of parents
                const subParents = this.getAllParents(edge.from, edges, visited);
                subParents.forEach(parent => parents.add(parent));
            }
        });
        return parents;
    }

    // Filter graph to show only branch (children and parents) of selected node/edge
    filterBranch() {
        const { selectedHistory, selectedEdge, graphData, network } = this.state;
        
        if (!graphData || !network) {
            this.setState({ 
                error: 'Veuillez d\'abord charger un graphique RFID' 
            });
            return;
        }

        let targetNodeId = null;
        
        // Determine target node ID
        if (selectedHistory) {
            targetNodeId = selectedHistory.history_id;
        } else if (selectedEdge) {
            // For edges, we'll focus on the "to" node (destination)
            targetNodeId = selectedEdge.to;
        } else {
            this.setState({ 
                error: 'Veuillez sélectionner un nœud ou une flèche pour filtrer la branche' 
            });
            return;
        }

        // Get all related nodes (children and parents)
        const allChildren = this.getAllChildren(targetNodeId, graphData.edges);
        const allParents = this.getAllParents(targetNodeId, graphData.edges);
        
        // Combine: target node + all children + all parents
        const branchNodeIds = new Set([targetNodeId]);
        allChildren.forEach(id => branchNodeIds.add(id));
        allParents.forEach(id => branchNodeIds.add(id));

        // Filter nodes
        const filteredNodes = graphData.nodes.filter(node => 
            branchNodeIds.has(node.history_id)
        );

        // Filter edges (only edges connecting filtered nodes)
        const filteredEdges = graphData.edges.filter(edge => 
            branchNodeIds.has(edge.from) && branchNodeIds.has(edge.to)
        );

        // Create filtered branch data
        const filteredBranch = {
            rfid: graphData.rfid,
            nodes: filteredNodes,
            edges: filteredEdges
        };

        // Store original data if not already stored
        if (!this.originalGraphData) {
            this.originalGraphData = graphData;
        }

        // Update state and re-render
        this.setState({ 
            graphData: filteredBranch,
            filteredBranch: filteredBranch,
            showingBranch: true,
            error: null
        }, () => {
            // Re-render graph with filtered data
            this.renderGraph(filteredBranch);
        });
    }

    // Reset branch filter to show full graph
    resetBranchFilter() {
        if (this.originalGraphData) {
            this.setState({ 
                graphData: this.originalGraphData,
                filteredBranch: null,
                showingBranch: false,
                error: null
            }, () => {
                // Re-render graph with original data
                this.renderGraph(this.originalGraphData);
            });
        }
    }

    render() {
        const { loading, error, graphData, selectedHistory, selectedEdge, rfidInput, rfidList, loadingList, listError, searchInput } = this.state;

        return (
            <CardPage>
                <Box p={2}>
                    <Typography variant="h4" gutterBottom>
                        Graphique de l'Historique RFID
                    </Typography>

                    {/* RFID Input Form */}
                    <Box mb={2}>
                        <form onSubmit={this.handleRFIDSubmit} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <TextField
                                label="Entrer un RFID"
                                value={rfidInput}
                                onChange={this.handleRFIDInputChange}
                                variant="outlined"
                                size="small"
                                placeholder="RFID..."
                                style={{ flex: 1, maxWidth: '400px' }}
                            />
                            <Button 
                                type="submit" 
                                variant="contained" 
                                color="primary"
                                disabled={!rfidInput.trim() || loading}
                            >
                                Charger
                            </Button>
                        </form>
                    </Box>

                    {loading && (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <CircularProgress />
                            <Typography variant="body1" style={{ marginLeft: 16 }}>
                                Chargement du graphique...
                            </Typography>
                        </Box>
                    )}

                    {error && (
                        <Alert severity="error" style={{ marginTop: 16 }}>
                            {error}
                        </Alert>
                    )}

                    {graphData && !loading && (
                        <>
                            <Box mb={2}>
                                <Typography variant="body2" color="textSecondary">
                                    RFID: <strong>{graphData.rfid}</strong> | 
                                    {graphData.nodes ? graphData.nodes.length : 0} mouvement(s) dans l'historique
                                    {graphData.edges && `, ${graphData.edges.length} lien(s)`}
                                </Typography>
                            </Box>

                            {/* Search field for history_id or transfer_id */}
                            <Box mb={2}>
                                <form onSubmit={this.handleSearchSubmit} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <TextField
                                        label="Rechercher un History ID ou Transfer ID"
                                        value={searchInput}
                                        onChange={this.handleSearchInputChange}
                                        variant="outlined"
                                        size="small"
                                        placeholder="history_id ou transfer_id..."
                                        style={{ flex: 1, maxWidth: '400px' }}
                                    />
                                    <Button 
                                        type="submit" 
                                        variant="outlined" 
                                        color="secondary"
                                        disabled={!searchInput.trim()}
                                    >
                                        Rechercher
                                    </Button>
                                </form>
                                <Typography variant="body2" color="textSecondary" style={{ marginTop: '8px' }}>
                                    Entrez un ID pour zoomer directement sur l'élément
                                </Typography>
                            </Box>

                            <Box mb={2} display="flex" alignItems="center" gap={1}>
                                <Button 
                                    variant="outlined" 
                                    onClick={() => this.changeLayout('LR')}
                                    style={{ marginRight: 8 }}
                                >
                                    Vue Horizontale (G → D)
                                </Button>
                                <Button 
                                    variant="outlined" 
                                    onClick={() => this.changeLayout('UD')}
                                    style={{ marginRight: 8 }}
                                >
                                    Vue Verticale (H → B)
                                </Button>
                                <Button 
                                    variant="outlined" 
                                    onClick={() => this.changeLayout('RL')}
                                    style={{ marginRight: 8 }}
                                >
                                    Vue Horizontale (D → G)
                                </Button>
                                <Button 
                                    variant="outlined" 
                                    onClick={() => {
                                        if (this.state.network) {
                                            this.state.network.fit();
                                        }
                                    }}
                                    style={{ marginRight: 8 }}
                                >
                                    Recentrer
                                </Button>
                                <Button 
                                    variant={this.state.showDeletedNodes ? "contained" : "outlined"}
                                    color={this.state.showDeletedNodes ? "default" : "primary"}
                                    onClick={() => {
                                        this.setState({ showDeletedNodes: !this.state.showDeletedNodes }, () => {
                                            // Re-render le graphique avec le nouveau filtre
                                            if (this.state.graphData) {
                                                this.renderGraph(this.state.graphData);
                                            }
                                        });
                                    }}
                                    title={this.state.showDeletedNodes ? "Masquer les nœuds supprimés" : "Afficher les nœuds supprimés"}
                                >
                                    {this.state.showDeletedNodes ? "Masquer supprimés" : "Afficher supprimés"}
                                </Button>
                            </Box>

                            <Paper style={{ padding: 16, marginBottom: 16 }}>
                                <div 
                                    ref={this.networkContainerRef}
                                    style={{ 
                                        width: '100%', 
                                        height: '600px', 
                                        border: '1px solid #ddd',
                                        borderRadius: 4
                                    }}
                                />
                            </Paper>

                            {selectedHistory && (
                                <>
                                    <Paper style={{ padding: 16, marginTop: 16 }}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                            <Typography variant="h6">
                                                Détails du Mouvement (Nœud)
                                            </Typography>
                                        <Box>
                                            {!this.state.showingBranch ? (
                                                <Button 
                                                    variant="contained" 
                                                    color="primary"
                                                    size="small"
                                                    onClick={() => this.filterBranch()}
                                                >
                                                    Filtrer la branche
                                                </Button>
                                            ) : (
                                                <Button 
                                                    variant="outlined" 
                                                    color="secondary"
                                                    size="small"
                                                    onClick={() => this.resetBranchFilter()}
                                                >
                                                    Afficher tout
                                                </Button>
                                            )}
                                        </Box>
                                    </Box>
                                    <Box style={{ userSelect: 'text', cursor: 'text' }}>
                                        <Typography variant="body2">
                                            <strong>History ID:</strong> {selectedHistory.history_id}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>RFID:</strong> {selectedHistory.rfid}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Transfer ID:</strong> {selectedHistory.transfer_id}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Type d'opération:</strong> {selectedHistory.op_type}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Date:</strong> {selectedHistory.date}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Produit:</strong> {selectedHistory.product_name} ({selectedHistory.product_id})
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Shop:</strong> {selectedHistory.shop_name} ({selectedHistory.shop_id})
                                        </Typography>
                                        {selectedHistory.from_shop && (
                                            <Typography variant="body2">
                                                <strong>Provenant de:</strong> {selectedHistory.from_shop_name} ({selectedHistory.from_shop})
                                            </Typography>
                                        )}
                                        {selectedHistory.op_type === 'reapprovisionnement_ext' && 
                                         (selectedHistory.dest_shop_name || selectedHistory.dest_account_name) && (
                                            <Box mt={1} mb={1} style={{ borderTop: '1px solid #ddd', paddingTop: '8px' }}>
                                                <Typography variant="body2" color="secondary">
                                                    <strong>Destination (Réappro Externe):</strong>
                                                </Typography>
                                                {selectedHistory.dest_shop_name && (
                                                    <Typography variant="body2">
                                                        <strong>Shop:</strong> {selectedHistory.dest_shop_name} ({selectedHistory.dest_shop_id})
                                                    </Typography>
                                                )}
                                                {selectedHistory.dest_account_name && (
                                                    <Typography variant="body2">
                                                        <strong>Compte:</strong> {selectedHistory.dest_account_name}
                                                    </Typography>
                                                )}
                                            </Box>
                                        )}
                                        <Typography variant="body2">
                                            <strong>Quantité:</strong> {selectedHistory.quantity}
                                        </Typography>
                                        <Box mt={1} mb={1} style={{ borderTop: '1px solid #ddd', paddingTop: '8px' }}>
                                            <Typography variant="body2" color="primary">
                                                <strong>Détails Prix:</strong>
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Prix Unitaire:</strong> {this.formatPriceUnit(selectedHistory.price_u || 0)}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Prix Unitaire TTC:</strong> {this.formatPriceUnit(this.calculatePriceUnitTTC(selectedHistory.price_ttc, selectedHistory.quantity))}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Prix HT:</strong> <Currency value={selectedHistory.price_ht || 0} />
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Prix TTC:</strong> <Currency value={selectedHistory.price_ttc || 0} />
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Coût:</strong> <Currency value={selectedHistory.cost || 0} />
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Surcout:</strong> {(selectedHistory.surcout || 0).toFixed(2)}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Transport:</strong> <Currency value={selectedHistory.transport || 0} />
                                                {selectedHistory.transport_total && (
                                                    <span> (<Currency value={selectedHistory.transport_total} />)</span>
                                                )}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Taxes:</strong> <Currency value={selectedHistory.taxes || 0} />
                                                {selectedHistory.taxes_total && (
                                                    <span> (<Currency value={selectedHistory.taxes_total} />)</span>
                                                )}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2">
                                            <strong>Status:</strong> {selectedHistory.status}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Disponible:</strong> {selectedHistory.available ? '✓ Oui' : '✗ Non'}
                                        </Typography>
                                    </Box>
                                </Paper>
                                
                                {/* Graphique d'évolution des prix */}
                                <PriceEvolutionChart 
                                    rfid={graphData.rfid}
                                    historyId={selectedHistory.history_id}
                                />
                            </>
                            )}

                            {selectedEdge && (
                                <Paper style={{ padding: 16, marginTop: 16 }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                        <Typography variant="h6">
                                            Détails du Transfer (Flèche)
                                        </Typography>
                                        <Box>
                                            {!this.state.showingBranch ? (
                                                <Button 
                                                    variant="contained" 
                                                    color="primary"
                                                    size="small"
                                                    onClick={() => this.filterBranch()}
                                                >
                                                    Filtrer la branche
                                                </Button>
                                            ) : (
                                                <Button 
                                                    variant="outlined" 
                                                    color="secondary"
                                                    size="small"
                                                    onClick={() => this.resetBranchFilter()}
                                                >
                                                    Afficher tout
                                                </Button>
                                            )}
                                        </Box>
                                    </Box>
                                    
                                    <Tabs 
                                        value={this.state.transferTabValue} 
                                        onChange={this.handleTransferTabChange}
                                        indicatorColor="primary"
                                        textColor="primary"
                                        style={{ marginBottom: 16 }}
                                    >
                                        <Tab label="Détails" />
                                        <Tab label="RFIDHistory" />
                                    </Tabs>

                                    {this.state.transferTabValue === 0 && (
                                        <Box style={{ userSelect: 'text', cursor: 'text' }}>
                                            <Typography variant="body2">
                                                <strong>Transfer ID:</strong> {selectedEdge.transfer_id}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Type:</strong> {selectedEdge.op_type}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Date:</strong> {selectedEdge.date}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Quantité:</strong> {selectedEdge.quantity}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Prix U:</strong> {this.formatPriceUnit(selectedEdge.price_u || 0)}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Prix Unitaire TTC:</strong> {this.formatPriceUnit(this.calculatePriceUnitTTC(selectedEdge.price_ttc, selectedEdge.quantity))}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Prix HT:</strong> <Currency value={selectedEdge.price_ht || 0} />
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Prix TTC:</strong> <Currency value={selectedEdge.price_ttc || 0} />
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Coût:</strong> <Currency value={selectedEdge.cost || 0} />
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Surcout:</strong> {(selectedEdge.surcout || 0).toFixed(2)}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Transport:</strong> <Currency value={selectedEdge.transport || 0} />
                                                {selectedEdge.transport_total && (
                                                    <span> (<Currency value={selectedEdge.transport_total} />)</span>
                                                )}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Taxes:</strong> <Currency value={selectedEdge.taxes || 0} />
                                                {selectedEdge.taxes_total && (
                                                    <span> (<Currency value={selectedEdge.taxes_total} />)</span>
                                                )}
                                            </Typography>
                                        </Box>
                                    )}

                                    {this.state.transferTabValue === 1 && (
                                        <Box>
                                            {this.state.loadingTransferHistory && (
                                                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                                                    <CircularProgress />
                                                    <Typography variant="body1" style={{ marginLeft: 16 }}>
                                                        Chargement des RFIDHistory...
                                                    </Typography>
                                                </Box>
                                            )}

                                            {this.state.transferHistoryError && (
                                                <Alert severity="error" style={{ marginBottom: 16 }}>
                                                    {this.state.transferHistoryError}
                                                </Alert>
                                            )}

                                            {!this.state.loadingTransferHistory && !this.state.transferHistoryError && (
                                                <>
                                                    {this.state.transferRFIDHistory.length > 0 ? (
                                                        <TableContainer component={Paper} style={{ maxHeight: 600 }}>
                                                            <Table stickyHeader size="small">
                                                                <TableHead>
                                                                    <TableRow>
                                                                        <TableCell><strong>RFID</strong></TableCell>
                                                                        <TableCell><strong>Produit</strong></TableCell>
                                                                        <TableCell><strong>Date</strong></TableCell>
                                                                        <TableCell><strong>Type</strong></TableCell>
                                                                        <TableCell><strong>Quantité</strong></TableCell>
                                                                        <TableCell><strong>Prix U</strong></TableCell>
                                                                        <TableCell><strong>Prix U TTC</strong></TableCell>
                                                                        <TableCell><strong>Prix HT</strong></TableCell>
                                                                        <TableCell><strong>Prix TTC</strong></TableCell>
                                                                        <TableCell><strong>Coût</strong></TableCell>
                                                                        <TableCell><strong>Shop</strong></TableCell>
                                                                        <TableCell><strong>Status</strong></TableCell>
                                                                        <TableCell><strong>Disponible</strong></TableCell>
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {this.state.transferRFIDHistory.map((history, index) => (
                                                                        <TableRow key={history.id || index} hover>
                                                                            <TableCell>{history.RFID || 'N/A'}</TableCell>
                                                                            <TableCell>
                                                                                {this.getProductName(history.product_id)}
                                                                                <Typography variant="caption" display="block" style={{ color: '#666', fontSize: '0.75rem' }}>
                                                                                    {history.product_id}
                                                                                </Typography>
                                                                            </TableCell>
                                                                            <TableCell>{this.formatDate(history.creation_date)}</TableCell>
                                                                            <TableCell>{history.transfer_type || 'N/A'}</TableCell>
                                                                            <TableCell>{history.quantity || 0}</TableCell>
                                                                            <TableCell>{this.formatPriceUnit(history.priceU || 0)}</TableCell>
                                                                            <TableCell>{this.formatPriceUnit(this.calculatePriceUnitTTC(history.price_ttc, history.quantity))}</TableCell>
                                                                            <TableCell><Currency value={history.price_ht || 0} /></TableCell>
                                                                            <TableCell><Currency value={history.price_ttc || 0} /></TableCell>
                                                                            <TableCell><Currency value={history.cost || 0} /></TableCell>
                                                                            <TableCell>{history.in_shop || 'N/A'}</TableCell>
                                                                            <TableCell>{history.status || 'N/A'}</TableCell>
                                                                            <TableCell>{history.available ? '✓ Oui' : '✗ Non'}</TableCell>
                                                                        </TableRow>
                                                                    ))}
                                                                </TableBody>
                                                            </Table>
                                                        </TableContainer>
                                                    ) : (
                                                        <Alert severity="info">
                                                            Aucun RFIDHistory trouvé pour ce transfer.
                                                        </Alert>
                                                    )}
                                                </>
                                            )}
                                        </Box>
                                    )}
                                </Paper>
                            )}
                        </>
                    )}

                    {!graphData && !loading && !error && (
                        <Alert severity="info">
                            Entrez un RFID ci-dessus pour afficher son historique de mouvements.
                        </Alert>
                    )}

                    {/* RFID List Section - Only show if productId is provided */}
                    {(this.props.productId || this.state.productId) && (
                        <Box mt={4}>
                            <Typography variant="h5" gutterBottom>
                                Liste de tous les RFID
                                <Typography variant="body2" color="textSecondary" component="span" style={{ marginLeft: 8 }}>
                                    (filtré par produit: {this.props.productId || this.state.productId})
                                </Typography>
                            </Typography>
                            
                            {loadingList && (
                                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                                    <CircularProgress />
                                    <Typography variant="body1" style={{ marginLeft: 16 }}>
                                        Chargement de la liste...
                                    </Typography>
                                </Box>
                            )}

                            {listError && (
                                <Alert severity="error" style={{ marginTop: 16 }}>
                                    {listError}
                                </Alert>
                            )}

                            {!loadingList && !listError && rfidList.length > 0 && (
                                <TableContainer component={Paper} style={{ marginTop: 16, maxHeight: 600 }}>
                                    <Table stickyHeader size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell><strong>RFID</strong></TableCell>
                                                <TableCell><strong>Date de création</strong></TableCell>
                                                <TableCell><strong>Quantitée</strong></TableCell>
                                                <TableCell><strong>Commentaires</strong></TableCell>
                                                <TableCell><strong>Action</strong></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {rfidList.map((item, index) => (
                                                <TableRow 
                                                    key={index} 
                                                    hover
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => this.handleRFIDClick(item.rfid)}
                                                >
                                                    <TableCell>{item.rfid}</TableCell>
                                                    <TableCell>{this.formatDate(item.creation_date)}</TableCell>
                                                    <TableCell>{item.quantity || item.quantitée || 'N/A'}</TableCell>
                                                    <TableCell>{item.comments || item.commentaires || 'N/A'}</TableCell>
                                                    <TableCell>
                                                        <Button 
                                                            variant="outlined" 
                                                            color="primary" 
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                this.handleRFIDClick(item.rfid);
                                                            }}
                                                        >
                                                            Voir le graphique
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}

                            {!loadingList && !listError && rfidList.length === 0 && (
                                <Alert severity="info" style={{ marginTop: 16 }}>
                                    Aucun RFID trouvé.
                                </Alert>
                            )}
                        </Box>
                    )}
                </Box>
            </CardPage>
        );
    }
}

// Map Redux state to props
function mapStateToProps(state) {
    return {
        currency: state.currency?.currency || 'Euro'
    };
}

// Connect RFIDGraph to Redux for currency
const ConnectedRFIDGraph = connect(mapStateToProps)(RFIDGraph);

// Update wrapper to use connected component
const RFIDGraphWrapperConnected = (props) => {
    const { rfid: rfidFromUrl } = useParams();
    const [searchParams] = useSearchParams();
    const productId = searchParams.get('product_id');
    const transferId = searchParams.get('transfer_id');
    const historyId = searchParams.get('history_id');
    
    return <ConnectedRFIDGraph rfidFromUrl={rfidFromUrl} productId={productId} transferId={transferId} historyId={historyId} {...props} />;
};

export default RFIDGraphWrapperConnected;

